import { V2TemplateListItemDto } from "../../../api/v2/templates";

export function parseJsonMaybe(v: any) {
    if (v == null) return null;
    if (typeof v === "string") {
        try {
            return JSON.parse(v);
        } catch {
            return null;
        }
    }
    return v;
}

export function getContentTypes(row: V2TemplateListItemDto): string[] {
    const raw = parseJsonMaybe(row.content_types_json);
    return Array.isArray(raw) ? raw.filter((x) => typeof x === "string") : [];
}

export function getTypeLabel(row: V2TemplateListItemDto): string {
    const types = getContentTypes(row);
    const cfg = parseJsonMaybe(row.types_config_json) || {};
    if (types.includes("twilio/list-picker")) return "Menu";
    if (types.includes("twilio/carousel")) return "Carousel";
    if (types.includes("twilio/call-to-action")) return "Text + Buttons";
    if (types.includes("twilio/card")) {
        const kind = cfg?.["twilio/card"]?.kind;
        if (kind === "image_text_buttons") return "Image + Text + Buttons";
        return "Card";
    }
    if (types.includes("twilio/text")) return "Text";
    return types.join(", ") || "-";
}

export function getDisplayStatus(row: V2TemplateListItemDto): string {
    const contentTypes = getContentTypes(row);
    const isMenu = contentTypes.includes("twilio/list-picker");
    const hasContent = !!row.provider_content_sid;
    if (isMenu && hasContent) return "ready";
    return String(row.status || "");
}

export function getStatusColor(status: string): "green" | "gold" | "red" | "blue" | "default" {
    const s = String(status || "").toLowerCase();
    if (s === "approved" || s === "ready") return "green";
    if (s === "pending") return "gold";
    if (s === "rejected") return "red";
    if (s === "draft") return "blue";
    return "default";
}

export function getRejectedReason(row: V2TemplateListItemDto): string | null {
    const status = String(getDisplayStatus(row) || "").toLowerCase();
    if (status !== "rejected") return null;
    const details = parseJsonMaybe(row?.status_details) || {};
    const reason = details?.whatsapp?.rejection_reason || details?.error?.message || null;
    const s = String(reason || "").trim();
    return s || null;
}

export function getPreviewText(row: V2TemplateListItemDto): string {
    const cfg = parseJsonMaybe(row.types_config_json) || {};
    const types = getContentTypes(row);

    if (types.includes("twilio/text")) return String(cfg?.["twilio/text"]?.body || "");
    if (types.includes("twilio/call-to-action")) return String(cfg?.["twilio/call-to-action"]?.body_text || cfg?.["twilio/call-to-action"]?.body || "");
    if (types.includes("twilio/list-picker")) return String(cfg?.["twilio/list-picker"]?.body_text || "");
    if (types.includes("twilio/card")) return String(cfg?.["twilio/card"]?.body_text || cfg?.["twilio/card"]?.body || "");
    if (types.includes("twilio/carousel")) {
        const top = String(cfg?.["twilio/carousel"]?.body_text || "");
        const cards = Array.isArray(cfg?.["twilio/carousel"]?.cards) ? cfg?.["twilio/carousel"]?.cards : [];
        const first = cards[0];
        const firstBody = String(first?.body_text || "");
        return [top, firstBody].filter(Boolean).join("\n");
    }
    return "";
}

export function hasImage(row: V2TemplateListItemDto): boolean {
    const cfg = parseJsonMaybe(row.types_config_json) || {};
    const types = getContentTypes(row);
    if (types.includes("twilio/card")) {
        const c = cfg?.["twilio/card"] || {};
        return (
            String(c?.image_source || "").toLowerCase() === "meta" ||
            !!String(c?.image_url || "").trim() ||
            (Array.isArray(c?.media) && c.media.length > 0)
        );
    }
    if (types.includes("twilio/carousel")) {
        const c = cfg?.["twilio/carousel"] || {};
        const cards = Array.isArray(c?.cards) ? c.cards : [];
        return cards.some((card: any) =>
            String(card?.image_source || "").toLowerCase() === "meta" ||
            !!String(card?.image_url || "").trim() ||
            (Array.isArray(card?.media) && card.media.length > 0)
        );
    }
    return false;
}

export function getRequiredTrackingContext(row: V2TemplateListItemDto): string[] {
    const out = new Set<string>();
    const cfg = parseJsonMaybe(row.types_config_json) || {};
    const contentTypes = getContentTypes(row);

    let requiresImage = false;
    let requiresUrlButton = false;

    if (contentTypes.includes("twilio/card")) {
        const c = cfg["twilio/card"] || {};
        if (String(c?.image_source || "").toLowerCase() === "meta") requiresImage = true;
        const actions = Array.isArray(c?.actions) ? c.actions : [];
        const metaUrlActions = actions.filter(
            (a: any) => String(a?.type || "").toLowerCase() === "url" && String(a?.url_source || "").toLowerCase() === "meta"
        );
        if (metaUrlActions.length > 0) requiresUrlButton = true;
    }

    if (contentTypes.includes("twilio/call-to-action")) {
        const c = cfg["twilio/call-to-action"] || {};
        const actions = Array.isArray(c?.actions) ? c.actions : [];
        const metaUrlActions = actions.filter(
            (a: any) => String(a?.type || "").toLowerCase() === "url" && String(a?.url_source || "").toLowerCase() === "meta"
        );
        if (metaUrlActions.length > 0) requiresUrlButton = true;
    }

    if (contentTypes.includes("twilio/carousel")) {
        const c = cfg["twilio/carousel"] || {};
        const cards = Array.isArray(c?.cards) ? c.cards : [];
        if (cards.some((card: any) => String(card?.image_source || "").toLowerCase() === "meta")) {
            requiresImage = true;
        }
        const allActions = cards.flatMap((card: any) => (Array.isArray(card?.actions) ? card.actions : []));
        const metaUrlActions = allActions.filter(
            (a: any) => String(a?.type || "").toLowerCase() === "url" && String(a?.url_source || "").toLowerCase() === "meta"
        );
        if (metaUrlActions.length > 0) requiresUrlButton = true;
    }

    if (requiresImage) out.add("context.images_urls[]");
    if (requiresUrlButton) out.add("context.buttons[]: [{ url, label? }]");

    return Array.from(out);
}
